import { useState, useEffect } from 'react';
import { instrumentAPI } from '../../../../../services';
import './QuickPresetModal.css';

function QuickPresetModal({ instrument, onClose, onSave }) {
  const [presets, setPresets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (instrument) {
      fetchPresets();
    }
  }, [instrument]);

  const fetchPresets = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await instrumentAPI.getTradingHoursPresets();
      if (response.data && response.data.presets) {
        // Filter presets by instrument type for better relevance
        const relevantPresets = response.data.presets.filter(preset => {
          const instrumentType = instrument.type.toLowerCase();
          const presetCategory = preset.category.toLowerCase();
          
          // Show all presets but prioritize matching category
          return true;
        }).sort((a, b) => {
          const instrumentType = instrument.type.toLowerCase();
          const aMatches = a.category.toLowerCase() === instrumentType;
          const bMatches = b.category.toLowerCase() === instrumentType;
          
          if (aMatches && !bMatches) return -1;
          if (!aMatches && bMatches) return 1;
          return a.name.localeCompare(b.name);
        });
        
        setPresets(relevantPresets);
      }
    } catch (err) {
      console.error('Error fetching presets:', err);
      
      // Handle specific error cases gracefully
      if (err.response?.status === 500) {
        console.warn('Presets service unavailable (500 error)');
        setError('Presets service is currently unavailable. Please try again later or use manual configuration.');
      } else if (err.response?.status === 404) {
        console.warn('Presets endpoint not found - feature may not be implemented yet');
        setError('Presets feature is not available. Please use manual trading hours configuration.');
      } else {
        console.warn('Failed to fetch presets');
        setError('Failed to load presets. Please check your connection and try again.');
      }
      
      // Set empty array so UI doesn't break
      setPresets([]);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyPreset = async (preset, mergeHolidays = false) => {
    setApplying(true);
    setError(null);

    try {
      const options = mergeHolidays ? { merge_holidays: true } : {};
      await instrumentAPI.applyPresetToInstrument(instrument.symbol, preset.name, options);
      
      onSave(); // Notify parent component
      onClose(); // Close modal
    } catch (err) {
      console.error('Error applying preset:', err);
      setError(`Failed to apply preset: ${err.message}`);
    } finally {
      setApplying(false);
    }
  };

  if (!instrument) return null;

  return (
    <div className="quick-preset-modal-overlay">
      <div className="quick-preset-modal">
        <div className="modal-header">
          <h3>
            <i className="fas fa-magic"></i>
            Quick Preset - {instrument.symbol}
          </h3>
          <button className="modal-close" onClick={onClose}>
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p>Loading presets...</p>
            </div>
          ) : (
            <>
              {error && (
                <div className="error-message">
                  <i className="fas fa-exclamation-triangle"></i>
                  {error}
                </div>
              )}

              <div className="preset-info">
                <p>
                  <strong>Instrument Type:</strong> {instrument.type}
                </p>
                <p>Select a preset to instantly configure trading hours for this instrument.</p>
              </div>

              <div className="presets-list">
                {presets.map(preset => {
                  const isRelevant = preset.category.toLowerCase() === instrument.type.toLowerCase();
                  
                  return (
                    <div 
                      key={preset.name} 
                      className={`preset-item ${isRelevant ? 'relevant' : ''}`}
                    >
                      <div className="preset-info-section">
                        <div className="preset-header">
                          <h4>{preset.name}</h4>
                          <div className="preset-badges">
                            <span className={`preset-category ${preset.category}`}>
                              {preset.category}
                            </span>
                            {preset.is_system_preset && (
                              <span className="system-badge">System</span>
                            )}
                            {isRelevant && (
                              <span className="recommended-badge">Recommended</span>
                            )}
                          </div>
                        </div>
                        <p className="preset-description">{preset.description}</p>
                      </div>
                      
                      <div className="preset-actions">
                        <button 
                          className="btn-small btn-primary"
                          onClick={() => handleApplyPreset(preset, false)}
                          disabled={applying}
                        >
                          <i className="fas fa-bolt"></i>
                          {applying ? 'Applying...' : 'Apply'}
                        </button>
                        <button 
                          className="btn-small btn-secondary"
                          onClick={() => handleApplyPreset(preset, true)}
                          disabled={applying}
                          title="Apply preset but keep existing holidays"
                        >
                          <i className="fas fa-plus"></i>
                          Merge
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {presets.length === 0 && (
                <div className="no-presets">
                  <i className="fas fa-info-circle"></i>
                  <p>No presets available. Please check your server configuration.</p>
                </div>
              )}
            </>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose} disabled={applying}>
            Cancel
          </button>
          <div className="footer-info">
            <i className="fas fa-info-circle"></i>
            <span>Recommended presets are shown first</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default QuickPresetModal; 